import { supabase } from "@/integrations/supabase/client";

export interface ActivityLogData {
  company_id: string;
  user_id: string;
  action_type: string;
  resource_type: string;
  resource_id?: string;
  resource_title?: string;
  details?: any;
  ip_address?: string;
  user_agent?: string;
}

export class ActivityLogger {
  private static async getClientInfo() {
    // Get IP address and user agent from the browser
    const userAgent = navigator.userAgent;
    
    // For IP address, we'll need to make a request to a service
    // In a real application, you might want to get this from your backend
    let ipAddress: string | undefined;
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      ipAddress = data.ip;
    } catch (error) {
      console.warn('Could not fetch IP address:', error);
    }

    return { ipAddress, userAgent };
  }

  static async logActivity(data: Omit<ActivityLogData, 'ip_address' | 'user_agent'>) {
    try {
      const { ipAddress, userAgent } = await this.getClientInfo();
      
      const { error } = await supabase.rpc('log_activity', {
        p_company_id: data.company_id,
        p_user_id: data.user_id,
        p_action_type: data.action_type,
        p_resource_type: data.resource_type,
        p_resource_id: data.resource_id || null,
        p_resource_title: data.resource_title || null,
        p_details: data.details || null,
        p_ip_address: ipAddress || null,
        p_user_agent: userAgent || null
      });

      if (error) {
        console.error('Error logging activity:', error);
      }
    } catch (error) {
      console.error('Error logging activity:', error);
    }
  }

  // Convenience methods for common actions
  static async logPolicyCreated(companyId: string, userId: string, policyId: string, policyTitle: string) {
    await this.logActivity({
      company_id: companyId,
      user_id: userId,
      action_type: 'policy_created',
      resource_type: 'policy',
      resource_id: policyId,
      resource_title: policyTitle,
      details: { action: 'Policy created successfully' }
    });
  }

  static async logPolicyEdited(companyId: string, userId: string, policyId: string, policyTitle: string, changes?: any) {
    await this.logActivity({
      company_id: companyId,
      user_id: userId,
      action_type: 'policy_edited',
      resource_type: 'policy',
      resource_id: policyId,
      resource_title: policyTitle,
      details: { action: 'Policy edited', changes }
    });
  }

  static async logPolicyStatusChanged(
    companyId: string, 
    userId: string, 
    policyId: string, 
    policyTitle: string, 
    fromStatus: string, 
    toStatus: string
  ) {
    await this.logActivity({
      company_id: companyId,
      user_id: userId,
      action_type: 'workflow_status_changed',
      resource_type: 'policy',
      resource_id: policyId,
      resource_title: policyTitle,
      details: { 
        action: 'Policy status changed',
        from_status: fromStatus,
        to_status: toStatus
      }
    });
  }

  static async logPolicyDeleted(companyId: string, userId: string, policyId: string, policyTitle: string) {
    await this.logActivity({
      company_id: companyId,
      user_id: userId,
      action_type: 'policy_deleted',
      resource_type: 'policy',
      resource_id: policyId,
      resource_title: policyTitle,
      details: { action: 'Policy deleted' }
    });
  }

  static async logPolicyTranslated(
    companyId: string,
    userId: string,
    originalPolicyId: string,
    newPolicyId: string,
    language: string,
    originalTitle: string
  ) {
    await this.logActivity({
      company_id: companyId,
      user_id: userId,
      action_type: 'policy_translated',
      resource_type: 'policy',
      resource_id: newPolicyId,
      resource_title: originalTitle,
      details: {
        action: `Policy translated to ${language}`,
        original_policy_id: originalPolicyId,
        new_policy_id: newPolicyId,
        language: language
      }
    });
  }

  static async logUserInvited(companyId: string, userId: string, invitedEmail: string, role: string) {
    await this.logActivity({
      company_id: companyId,
      user_id: userId,
      action_type: 'user_invited',
      resource_type: 'user',
      resource_title: invitedEmail,
      details: { 
        action: 'User invited',
        invited_email: invitedEmail,
        role: role
      }
    });
  }

  static async logUserJoined(companyId: string, userId: string, userName: string, userEmail: string) {
    await this.logActivity({
      company_id: companyId,
      user_id: userId,
      action_type: 'user_joined',
      resource_type: 'user',
      resource_title: userName,
      details: { 
        action: 'User joined',
        user_name: userName,
        user_email: userEmail
      }
    });
  }
}
